// src/components/UnitSelect.js
// #398, Session 78n2. The one unit picker, used by every screen that asks a
// person which unit something is in.
//
// It exists because there were SIX vocabularies before it: four hardcoded lists
// (InventoryModals.js, RequestMaterialsPage.js, CatalogPromotionModal.js and
// PurchasingQueuePage.js — three of them the same nine words in three different
// orders, one of them a DIFFERENT nine), one Postgres enum, and two free-text
// inputs. Building the picker seven times is how they drifted apart the first
// time, so it is built once here.
//
// Two kinds, because the standard splits them and so does the data:
//   kind="measure" — a STOCKING unit. "What is one of these?" sheet, gallon.
//                    UN/CEFACT Rec 20. Lives on inventory_items.uom.
//   kind="package" — a PACKAGE type. "How does the vendor ship it?" case,
//                    pallet. UN/CEFACT Rec 21. Lives on vendor_items.pack_unit.
// A unit whose kind is 'both' is offered by both — vinyl is stocked by the roll
// and ships by the roll, and the census found box on 2 real production items.
//
// THE INLINE ADD IS THE POINT, not a convenience. Colby's requirement: Jennifer
// cannot sit in the middle of an order waiting for someone else to enter a new
// unit. So "+ Add a unit" sits at the bottom of the list itself and opens a
// panel over the sheet she is already in — she never navigates away, and the new
// unit comes back already selected. This is NetSuite's inline-record-create
// pattern, and Stede already does it for vendors inside Mark as ordered.
//
// Deliberately NO code field on the inline add. Jennifer knows the word, not the
// UN standard code for it, and asking would either block her or fill the column
// with guesses. An executive fills codes in later from Company Settings, where
// the whole list is in front of them.
//
// Add only. There is no delete and no rename here — see canAddUnit().

import { useState, useEffect, useCallback } from 'react';
import Overlay from './Overlay';
import { useAuth } from './AuthContext';
import { fetchUnits, addUnit } from '../lib/taxonomy';
import { canAddUnit } from '../lib/permissions';

const GREEN = '#1D9E75';
const ADD_VALUE = '__add_unit__';

// A failed load must not render as an empty dropdown. An empty unit list reads
// as "there are no units" and actually means the query broke — the
// silently-empty failure mode this project keeps getting bitten by. The error is
// returned so the caller can say so rather than showing a blank picker.
export function useUnits(kind) {
  const [units, setUnits] = useState([]);
  const [unitsError, setUnitsError] = useState('');

  const reloadUnits = useCallback(async () => {
    const { rows, error } = await fetchUnits({ activeOnly: true, kind });
    if (error) { setUnitsError('Could not load the unit list.'); return; }
    setUnitsError('');
    setUnits(rows);
  }, [kind]);

  useEffect(() => { reloadUnits(); }, [reloadUnits]);

  return { units, unitsError, reloadUnits };
}

export default function UnitSelect({
  kind,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select a unit',
  style,
  ariaLabel,
}) {
  const auth = useAuth();
  const { units, unitsError, reloadUnits } = useUnits(kind);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');

  const mayAdd = canAddUnit(auth);

  // A value stored before this list governed the field may no longer be on it —
  // a retired unit, or a word typed into the old free-text input. Showing it as
  // a disabled option rather than dropping it means the field renders what is
  // actually saved instead of silently appearing blank, which is how a wrong
  // value gets committed by someone who never touched the control.
  const knownNames = units.map(u => u.name);
  const orphaned = value && !knownNames.includes(value) ? value : null;

  function handleChange(e) {
    const next = e.target.value;
    if (next === ADD_VALUE) { setAddError(''); setNewName(''); setAdding(true); return; }
    onChange(next);
  }

  async function handleAdd() {
    const trimmed = newName.trim();
    if (!trimmed) { setAddError('Enter a name first.'); return; }
    setSaving(true);
    // kind is taken from the picker rather than asked. A person adding a unit
    // from the "Vendor sells by" field is answering the packaging question by
    // definition, and asking them to classify it is exactly the confusion
    // between the two fields this whole issue is about.
    const { error } = await addUnit(trimmed, kind, null);
    setSaving(false);
    if (error) {
      setAddError(
        /duplicate|unique/i.test(error.message || '')
          ? `"${trimmed}" is already on the list — pick it from the dropdown.`
          : (error.message || 'Could not add that unit.')
      );
      return;
    }
    await reloadUnits();
    onChange(trimmed);
    setAdding(false);
  }

  return (
    <>
      <select
        value={value || ''}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel}
        style={{
          width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 8,
          padding: '0 10px', fontSize: 16, background: disabled ? '#F5F5F3' : 'white',
          color: '#0F0F0F', boxSizing: 'border-box',
          // NO appearance:'none' here, deliberately, and it is a correctness
          // decision rather than a style one. Raised by Colby the first time he
          // saw this control: the app's other inventory selects strip the native
          // chevron, and this one copied them. That is tolerable on a field that
          // has always been a dropdown - and wrong on THIS one, because it just
          // stopped being free text. Without the arrow it still reads as a box
          // you type into, so the person never discovers the list, which is the
          // entire point of #398. The affordance is the feature.
          ...style,
        }}
      >
        <option value="">{unitsError ? 'Unit list unavailable' : placeholder}</option>
        {orphaned && <option value={orphaned}>{orphaned} (no longer on the list)</option>}
        {units.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
        {mayAdd && !unitsError && <option value={ADD_VALUE}>+ Add a unit...</option>}
      </select>

      {unitsError && (
        <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 4 }}>{unitsError}</div>
      )}

      {adding && (
        <UnitAddPanel
          kind={kind}
          name={newName}
          onNameChange={v => { setNewName(v); if (addError) setAddError(''); }}
          onCancel={() => setAdding(false)}
          onSubmit={handleAdd}
          saving={saving}
          error={addError}
        />
      )}
    </>
  );
}

// Top-level, not defined inside UnitSelect's body — a component declared inside
// a parent remounts on every render and drops input focus mid-typing, which on
// this panel would mean losing the word after the first letter.
function UnitAddPanel({ kind, name, onNameChange, onCancel, onSubmit, saving, error }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isPackage = kind === 'package';

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={onCancel} maxWidth={400}>
      <div style={{ padding: 18, maxHeight: isMobile ? undefined : '80vh', overflowY: isMobile ? undefined : 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 4 }}>
          {isPackage ? 'Add a package type' : 'Add a stocking unit'}
        </div>
        <p style={{ fontSize: 13, color: '#5C5C5C', margin: '0 0 12px', lineHeight: 1.5 }}>
          {isPackage
            ? 'How the vendor ships it — a crate, a spool. It joins the company list straight away and everyone else can use it.'
            : 'What one of the item is — a sheet, a gallon. It joins the company list straight away and everyone else can use it.'}
        </p>

        <input
          type="text"
          value={name}
          autoFocus
          onChange={e => onNameChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !saving) onSubmit(); }}
          placeholder={isPackage ? 'e.g. crate' : 'e.g. board foot'}
          style={{
            width: '100%', height: 44, border: '1px solid #E0E0E0', borderRadius: 8,
            padding: '0 12px', fontSize: 16, boxSizing: 'border-box', marginBottom: 12,
          }}
        />

        {error && (
          <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 8, border: '1px solid #E0E0E0',
              background: 'white', color: '#5C5C5C', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={saving}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 8, border: 'none',
              background: saving ? '#E0E0E0' : GREEN,
              color: saving ? '#9A9A9A' : 'white',
              fontSize: 15, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Adding...' : 'Add and use it'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
